import { SetStateAction, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { REACT_APP_GLOBAL_API_BASE_URL_V2 } from 'runtimeEnvVars';

import { addDefaultErrorMessageAction } from 'containers/FlashMessage/actions';
import { apiGet } from 'utils/axios';

import { Filterable } from '../../util';

interface Owner {
  id: string;
  name: string;
}

/**
 * Fetches the distinct owners of the host's accessible openings from
 * `/openings/owners`. Search is filtered client-side via `setQuery`.
 */
export const useOpeningOwners = (userId?: string) => {
  const dispatch = useDispatch();
  const [query, setQuery] = useState('');
  const [owners, setOwners] = useState<Filterable[]>([]);

  useEffect(() => {
    const fetchOwners = async () => {
      try {
        const { data } = await apiGet(
          `${REACT_APP_GLOBAL_API_BASE_URL_V2}/openings/owners`,
          { user_id: userId },
        );
        // The endpoint (ApiSelfServe::V2::OpeningsController#owners) renders a bare
        // array of { id, name }, not an object with an `owners` key.
        const ownersData = (Array.isArray(data) ? data : data?.owners) as
          | Owner[]
          | undefined;
        const filterableOwners = (ownersData ?? []).map(owner => ({
          title: owner.name,
          id: owner.id,
        }));
        setOwners(filterableOwners as SetStateAction<Filterable[]>);
      } catch {
        dispatch(addDefaultErrorMessageAction());
      }
    };

    void fetchOwners();
  }, [userId, dispatch]);

  const filteredOwners = useMemo(
    () =>
      owners.filter(owner =>
        owner.title.toLowerCase().includes(query.toLowerCase()),
      ),
    [owners, query],
  );

  return { owners: filteredOwners, setQuery };
};
